import { type ChangeEvent, type FormEvent, type ReactElement, useState } from 'react';
import type {
  AdjacentProductSearchResponse,
  FirstPartyIntegrationKey,
  SavedSearch,
  SearchCardResult,
  SearchFullCardResult,
} from '@molt/shared';
import {
  Archive,
  CalendarDays,
  CheckCircle2,
  ExternalLink,
  Pencil,
  Search,
  Trash2,
} from 'lucide-react';
import { Button, EmptyState, Notice, Skeleton, TextField } from '@/components';
import {
  dueStatus,
  dueStatusLabel,
  parseCardTimestamp,
  type DueStatus,
} from '@/lib/card-dates';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  EMPTY_SEARCH_SIDE_FILTER_STATE,
  type SearchSideFilterState,
} from '@/lib/search-operators';
import { OperatorHelp } from './OperatorHelp';

/**
 * FE-33.1 — the row this page draws, and **an intersection rather than either
 * of the two schemas on its own**.
 *
 * `GET /search/results` now answers `searchFullResultsPageSchema`, whose rows
 * carry `dueAt` and `completed` (`FR-SEARCH-017`'s edge case: *"Cards whose due
 * date is marked complete are visually distinguished in the result list"*).
 * `apps/web/src/lib/search.ts` still parses the response against the older
 * `searchFullResultsSchema`, whose `cards` are a `z.object` and therefore
 * **strip both fields at the client boundary** — that one `get(...)` call is
 * the whole of the residual, and it is not this chunk's file.
 *
 * So the prop is written so that BOTH shapes are assignable to it:
 *
 *   - `SearchCardResult` — what `routes/search.tsx` passes today, which is why
 *     that file compiles unchanged and was not opened.
 *   - `SearchFullCardResult` — what it will pass the moment `lib/search.ts`
 *     re-points, which is why THIS file needs no further edit then.
 *
 * ⚠ `dueAt` may legitimately be `null` (a card with no due date), so `null` and
 * `undefined` are not interchangeable here: `undefined` means *the reader did
 * not project the field*, and `cardDueStatus` discriminates on `completed`,
 * which is `NOT NULL` on the wire and therefore the honest presence signal.
 */
export type SearchResultsPageCard = SearchCardResult &
  Partial<Pick<SearchFullCardResult, 'dueAt' | 'completed'>>;

export type SearchResultsSort = SearchSideFilterState['sort'];
export type SearchResultsDateRange = SearchSideFilterState['edited'];

export interface SearchResultsPageProps {
  readonly query: string;
  readonly cards: readonly SearchResultsPageCard[];
  readonly loading: boolean;
  readonly error?: string;
  readonly includeArchived: boolean;
  readonly includeClosedBoards: boolean;
  /**
   * `FR-SEARCH-017`'s third side toggle — *"a toggle for whether card
   * descriptions are matched"*.
   *
   * ⚠ **Required, not optional, and the wire default is `true`.** The route's
   * `matchDescriptions` querystring member defaults to `true`, so an optional
   * prop defaulting to `undefined` here would render an unchecked box over a
   * search that *was* matching descriptions — a control that lies about the
   * request it produced. Making it required puts the obligation on the two
   * construction sites (`routes/search.tsx` and this file's own test) at
   * compile time instead.
   */
  readonly matchDescriptions: boolean;
  readonly filters: SearchSideFilterState;
  readonly savedSearches: readonly SavedSearch[];
  readonly savedSearchesLoading: boolean;
  readonly savedSearchesError?: string;
  readonly savedSearchActionError?: string;
  readonly savingSavedSearch: boolean;
  readonly renamingSavedSearchId?: string;
  readonly deletingSavedSearchId?: string;
  readonly runningSavedSearchId?: string;
  readonly adjacentProductSearch: AdjacentProductSearchResponse | null;
  readonly adjacentProductSearchLoading: boolean;
  readonly adjacentProductSearchError?: string;
  readonly activeAdjacentProductProvider?: FirstPartyIntegrationKey;
  readonly onQueryChange: (query: string) => void;
  readonly onIncludeArchivedChange: (include: boolean) => void;
  readonly onIncludeClosedBoardsChange: (include: boolean) => void;
  readonly onMatchDescriptionsChange: (matchDescriptions: boolean) => void;
  readonly onFiltersChange: (patch: Partial<SearchSideFilterState>) => void;
  readonly onRetry: () => void;
  readonly onSaveSearch: (name: string) => void;
  readonly onRenameSavedSearch: (id: string, name: string) => void;
  readonly onDeleteSavedSearch: (id: string) => void;
  readonly onRunSavedSearch: (id: string) => void;
  readonly onAdjacentProductProviderChange: (
    provider: FirstPartyIntegrationKey | undefined,
  ) => void;
  readonly onAdjacentProductRetry: () => void;
}

const SORT_OPTIONS: readonly { value: SearchResultsSort; labelKey: string }[] = [
  { value: 'created', labelKey: 'search.results.sort.created' },
  { value: 'updated', labelKey: 'search.results.sort.updated' },
  { value: 'due', labelKey: 'search.results.sort.due' },
];

const TEXT_FILTER_INPUTS: readonly {
  key: 'board' | 'list' | 'member' | 'label';
  labelKey: string;
  placeholderKey: string;
}[] = [
  {
    key: 'board',
    labelKey: 'search.results.filter.board',
    placeholderKey: 'search.results.filter.boardPlaceholder',
  },
  {
    key: 'list',
    labelKey: 'search.results.filter.list',
    placeholderKey: 'search.results.filter.listPlaceholder',
  },
  {
    key: 'member',
    labelKey: 'search.results.filter.member',
    placeholderKey: 'search.results.filter.memberPlaceholder',
  },
  {
    key: 'label',
    labelKey: 'search.results.filter.label',
    placeholderKey: 'search.results.filter.labelPlaceholder',
  },
];

const HAS_OPTIONS: readonly { value: SearchSideFilterState['has']; labelKey: string }[] =
  [
    { value: 'any', labelKey: 'search.results.has.any' },
    { value: 'attachments', labelKey: 'search.results.has.attachments' },
    { value: 'description', labelKey: 'search.results.has.description' },
    { value: 'cover', labelKey: 'search.results.has.cover' },
    { value: 'members', labelKey: 'search.results.has.members' },
    { value: 'stickers', labelKey: 'search.results.has.stickers' },
  ];

const DUE_OPTIONS: readonly { value: SearchSideFilterState['due']; labelKey: string }[] =
  [
    { value: 'any', labelKey: 'search.results.due.any' },
    { value: 'day', labelKey: 'search.results.due.day' },
    { value: 'week', labelKey: 'search.results.due.week' },
    { value: 'month', labelKey: 'search.results.due.month' },
    { value: 'overdue', labelKey: 'search.results.due.overdue' },
    { value: 'complete', labelKey: 'search.results.due.complete' },
    { value: 'incomplete', labelKey: 'search.results.due.incomplete' },
  ];

const CREATED_OPTIONS: readonly {
  value: SearchSideFilterState['created'];
  labelKey: string;
}[] = [
  { value: 'any', labelKey: 'search.results.time.any' },
  { value: 'day', labelKey: 'search.results.time.day' },
  { value: 'week', labelKey: 'search.results.time.week' },
  { value: 'month', labelKey: 'search.results.time.month' },
];

const EDITED_OPTIONS: readonly {
  value: SearchSideFilterState['edited'];
  labelKey: string;
}[] = [...CREATED_OPTIONS, { value: 'year', labelKey: 'search.results.time.year' }];

const STATUS_OPTIONS: readonly {
  value: SearchSideFilterState['status'];
  labelKey: string;
}[] = [
  { value: 'any', labelKey: 'search.results.status.any' },
  { value: 'open', labelKey: 'search.results.status.open' },
  { value: 'archived', labelKey: 'search.results.status.archived' },
];

const SELECT_CLASS =
  'h-9 rounded-card border border-border-strong bg-surface px-3 text-sm text-ink shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20';

const INPUT_CLASS =
  'h-9 rounded-card border border-border-strong bg-surface px-3 text-sm text-ink shadow-sm placeholder:text-ink-muted focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20';

const RESULT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

function formatDate(value: string | null | undefined, locale: string): string | null {
  if (value === null || value === undefined) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, RESULT_DATE_FORMAT).format(date);
}

/**
 * The due date is parsed by `parseCardTimestamp`, not by `new Date`, and that
 * is the one parser `cardDueStatus` uses too — a badge whose colour is decided
 * from one reading of the string and whose text is decided from another is how
 * "Overdue" ends up beside next month's date with nothing red anywhere.
 *
 * `lib/card-dates.ts`'s own `formatCardDate` is deliberately NOT reused: its
 * docstring records that its `undefined` locale follows the BROWSER rather than
 * the interface language, which would put the due date in one locale beside
 * `search.results.updated`'s date in another, in the same row.
 */
function formatDueDate(value: string | null | undefined, locale: string): string | null {
  if (value === null || value === undefined) return null;
  const date = parseCardTimestamp(value);
  if (date === null) return null;
  return new Intl.DateTimeFormat(locale, RESULT_DATE_FORMAT).format(date);
}

/**
 * `'none'` covers two different things on purpose — a card with no due date,
 * and a row from a reader that does not project the field at all — because the
 * page draws nothing in either case. `completed` is the discriminator rather
 * than `dueAt`, since `dueAt: null` is a legal projected value and
 * `completed: true` with no due date is a legal card (`FR-CARD-004`; there is
 * no CHECK tying the two columns together).
 */
function cardDueStatus(card: SearchResultsPageCard): DueStatus {
  if (card.completed === undefined) return 'none';
  return dueStatus({ dueAt: card.dueAt ?? null, completed: card.completed });
}

/**
 * Mirrors `dueStateClasses` in `components/board/CardBadges.tsx` — the same
 * five tones the card front already uses, so a card that is overdue on the
 * board is overdue in the same colour in the result list. That function is not
 * exported and `CardBadges.tsx` is not this chunk's file, so this is a copy and
 * is written as one rather than smuggled in as an import.
 */
function dueToneClass(status: DueStatus): string {
  switch (status) {
    case 'complete':
      return 'text-ok';
    case 'due-soon':
      return 'text-warn';
    case 'overdue':
    case 'long-overdue':
      return 'text-bad';
    case 'neutral':
    case 'none':
      return 'text-ink-muted';
  }
}

function cardIsArchived(card: SearchResultsPageCard): boolean {
  return card.archived || card.list.archived;
}

function cardTitle(card: SearchResultsPageCard, fallback: string): string {
  return card.title.trim() || fallback;
}

function resultMeta(card: SearchResultsPageCard): string {
  return `${card.board.title} / ${card.list.title}`;
}

function textFilterValue(value: string): string | null {
  const next = value.trim();
  return next.length === 0 ? null : next;
}

function savedSearchName(savedSearch: SavedSearch, fallback: string): string {
  return savedSearch.name.trim() || fallback;
}

export function SearchResultsPage({
  query,
  cards,
  loading,
  error,
  includeArchived,
  includeClosedBoards,
  matchDescriptions,
  filters = EMPTY_SEARCH_SIDE_FILTER_STATE,
  savedSearches,
  savedSearchesLoading,
  savedSearchesError,
  savedSearchActionError,
  savingSavedSearch,
  renamingSavedSearchId,
  deletingSavedSearchId,
  runningSavedSearchId,
  adjacentProductSearch,
  adjacentProductSearchLoading,
  adjacentProductSearchError,
  activeAdjacentProductProvider,
  onQueryChange,
  onIncludeArchivedChange,
  onIncludeClosedBoardsChange,
  onMatchDescriptionsChange,
  onFiltersChange,
  onRetry,
  onSaveSearch,
  onRenameSavedSearch,
  onDeleteSavedSearch,
  onRunSavedSearch,
  onAdjacentProductProviderChange,
  onAdjacentProductRetry,
}: SearchResultsPageProps): ReactElement {
  const { locale, t } = useInterfaceLanguage();
  const [savedSearchNameInput, setSavedSearchNameInput] = useState('');
  const [editingSavedSearchId, setEditingSavedSearchId] = useState<string | null>(null);
  const [editingSavedSearchName, setEditingSavedSearchName] = useState('');
  const adjacentTabs = adjacentProductSearch?.tabs ?? [];
  const activeAdjacentProviderResult =
    activeAdjacentProductProvider === undefined
      ? undefined
      : adjacentProductSearch?.providers.find(
          (provider) => provider.provider === activeAdjacentProductProvider,
        );

  function submitQuery(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const value = formData.get('q');
    onQueryChange(typeof value === 'string' ? value : '');
  }

  function submitSavedSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = savedSearchNameInput.trim();
    if (name.length === 0 || query.trim().length === 0) return;
    onSaveSearch(name);
    setSavedSearchNameInput('');
  }

  function startRename(savedSearch: SavedSearch) {
    setEditingSavedSearchId(savedSearch.id);
    setEditingSavedSearchName(
      savedSearchName(savedSearch, t('search.results.saved.untitled')),
    );
  }

  function cancelRename() {
    setEditingSavedSearchId(null);
    setEditingSavedSearchName('');
  }

  function submitRename(event: FormEvent<HTMLFormElement>, savedSearch: SavedSearch) {
    event.preventDefault();
    const name = editingSavedSearchName.trim();
    if (name.length === 0) return;
    onRenameSavedSearch(savedSearch.id, name);
    cancelRename();
  }

  function providerPanelMessage(
    state: 'ready' | 'not_linked' | 'no_access',
    name: string,
  ) {
    if (state === 'not_linked') {
      return t('search.results.adjacent.notLinked', { provider: name });
    }
    if (state === 'no_access') {
      return t('search.results.adjacent.noAccess', { provider: name });
    }
    return t('search.results.adjacent.noMatches', { provider: name });
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6">
      <header className="flex flex-col gap-3">
        <p className="text-sm font-medium text-ink-muted">{t('search.label')}</p>
        <h1 className="text-3xl font-semibold text-ink">{t('search.results.title')}</h1>
      </header>

      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={submitQuery}
      >
        <TextField
          key={query}
          label={t('search.results.queryLabel')}
          name="q"
          defaultValue={query}
          leadingIcon={<Search aria-hidden="true" className="size-4" />}
          wrapperClassName="flex-1"
        />
        <Button type="submit" variant="primary">
          {t('search.label')}
        </Button>
      </form>
      <OperatorHelp query={query} onQueryChange={onQueryChange} />

      <div className="grid gap-5 lg:grid-cols-[17rem_minmax(0,1fr)]">
        <div className="flex flex-col gap-4 border-y border-border-strong py-4 lg:border-y-0 lg:border-r lg:py-0 lg:pr-5">
          <aside aria-label={t('search.results.filters')} className="flex flex-col gap-4">
            {TEXT_FILTER_INPUTS.map((input) => (
              <label key={input.key} className="flex flex-col gap-1 text-sm text-ink">
                <span className="font-medium">{t(input.labelKey)}</span>
                <input
                  type="text"
                  className={INPUT_CLASS}
                  value={filters[input.key] ?? ''}
                  placeholder={t(input.placeholderKey)}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const value = textFilterValue(event.currentTarget.value);
                    if (input.key === 'board') onFiltersChange({ board: value });
                    if (input.key === 'list') onFiltersChange({ list: value });
                    if (input.key === 'member') onFiltersChange({ member: value });
                    if (input.key === 'label') onFiltersChange({ label: value });
                  }}
                />
              </label>
            ))}

            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="mt-1 size-4 accent-accent"
                checked={filters.starredOnly}
                onChange={(event) =>
                  onFiltersChange({ starredOnly: event.currentTarget.checked })
                }
              />
              <span>
                {t('search.results.starredOnly')}
                <span className="block text-xs text-ink-muted">
                  {t('search.results.starredOnlyHelp')}
                </span>
              </span>
            </label>

            <label className="flex flex-col gap-1 text-sm text-ink">
              <span className="font-medium">{t('search.results.cardAttribute')}</span>
              <select
                className={SELECT_CLASS}
                value={filters.has}
                onChange={(event) =>
                  onFiltersChange({
                    has: event.currentTarget.value as SearchSideFilterState['has'],
                  })
                }
              >
                {HAS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm text-ink">
              <span className="font-medium">{t('search.results.dueDate')}</span>
              <select
                className={SELECT_CLASS}
                value={filters.due}
                onChange={(event) =>
                  onFiltersChange({
                    due: event.currentTarget.value as SearchSideFilterState['due'],
                  })
                }
              >
                {DUE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm text-ink">
              <span className="font-medium">{t('search.results.createdWithin')}</span>
              <select
                className={SELECT_CLASS}
                value={filters.created}
                onChange={(event) =>
                  onFiltersChange({
                    created: event.currentTarget
                      .value as SearchSideFilterState['created'],
                  })
                }
              >
                {CREATED_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm text-ink">
              <span className="font-medium">{t('search.results.editedWithin')}</span>
              <select
                className={SELECT_CLASS}
                value={filters.edited}
                onChange={(event) =>
                  onFiltersChange({
                    edited: event.currentTarget.value as SearchSideFilterState['edited'],
                  })
                }
              >
                {EDITED_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm text-ink">
              <span className="font-medium">{t('search.results.cardStatus')}</span>
              <select
                className={SELECT_CLASS}
                value={filters.status}
                onChange={(event) =>
                  onFiltersChange({
                    status: event.currentTarget.value as SearchSideFilterState['status'],
                  })
                }
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </label>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium text-ink">
                {t('search.results.sort')}
              </legend>
              <div className="inline-flex rounded-card border border-border-strong bg-surface-raised p-0.5">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={
                      filters.sort === option.value
                        ? 'h-8 flex-1 rounded-card bg-accent px-2 text-sm font-medium text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
                        : 'h-8 flex-1 rounded-card px-2 text-sm font-medium text-ink-muted hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
                    }
                    aria-pressed={filters.sort === option.value}
                    onClick={() => onFiltersChange({ sort: option.value })}
                  >
                    {t(option.labelKey)}
                  </button>
                ))}
              </div>
            </fieldset>

            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="mt-1 size-4 accent-accent"
                checked={includeArchived}
                onChange={(event) => onIncludeArchivedChange(event.currentTarget.checked)}
              />
              <span>
                {t('search.results.includeArchived')}
                <span className="block text-xs text-ink-muted">
                  {t('search.results.includeArchivedHelp')}
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="mt-1 size-4 accent-accent"
                checked={includeClosedBoards}
                onChange={(event) =>
                  onIncludeClosedBoardsChange(event.currentTarget.checked)
                }
              />
              <span>
                {t('search.results.includeClosedBoards')}
                <span className="block text-xs text-ink-muted">
                  {t('search.results.includeClosedBoardsHelp')}
                </span>
              </span>
            </label>

            {/* FE-33.1 — the fourth checkbox, and the only one of the four that
                is checked by DEFAULT. `FR-SEARCH-017` asks for "a toggle for
                whether card descriptions are matched"; the route matches them
                unless told otherwise, so the honest resting state of this
                control is ON, and unticking it NARROWS the search. It sits with
                the other two lifecycle toggles rather than in the operator
                group above because the requirement lists all three in one
                breath, and because both of its neighbours are likewise
                "what does this search reach" rather than "what does it look
                like". */}
            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="mt-1 size-4 accent-accent"
                checked={matchDescriptions}
                onChange={(event) =>
                  onMatchDescriptionsChange(event.currentTarget.checked)
                }
              />
              <span>
                {t('search.results.matchDescriptions')}
                <span className="block text-xs text-ink-muted">
                  {t('search.results.matchDescriptionsHelp')}
                </span>
              </span>
            </label>
          </aside>

          <section
            aria-label={t('search.results.saved.title')}
            className="flex flex-col gap-3 border-t border-border-strong pt-4"
          >
            <div className="space-y-1">
              <h2 className="text-sm font-semibold text-ink">
                {t('search.results.saved.title')}
              </h2>
              <p className="text-xs text-ink-muted">
                {t('search.results.saved.description')}
              </p>
            </div>

            <form className="space-y-2" onSubmit={submitSavedSearch}>
              <TextField
                label={t('search.results.saved.nameLabel')}
                value={savedSearchNameInput}
                onChange={(event) => setSavedSearchNameInput(event.currentTarget.value)}
                placeholder={t('search.results.saved.placeholder')}
              />
              <Button
                type="submit"
                size="sm"
                variant="secondary"
                busy={savingSavedSearch}
                busyLabel={t('search.results.saved.busy')}
                disabled={query.trim().length === 0}
                disabledReason={
                  query.trim().length === 0
                    ? t('search.results.saved.disabledBlank')
                    : undefined
                }
                className="w-full"
              >
                {t('search.results.saved.save')}
              </Button>
            </form>

            {savedSearchActionError !== undefined ? (
              <Notice
                tone="bad"
                title={t('search.results.saved.actionFailed')}
                body={savedSearchActionError}
              />
            ) : null}

            {savedSearchesError !== undefined ? (
              <Notice
                tone="bad"
                title={t('search.results.saved.loadFailed')}
                body={savedSearchesError}
              />
            ) : savedSearchesLoading ? (
              <div aria-label={t('search.results.saved.loading')} aria-busy="true">
                <Skeleton shape="row" count={3} />
              </div>
            ) : savedSearches.length === 0 ? (
              <p className="rounded-card border border-border bg-surface-raised px-3 py-2 text-sm text-ink-muted">
                {t('search.results.saved.empty')}
              </p>
            ) : (
              <ol className="space-y-2">
                {savedSearches.map((savedSearch) => {
                  const name = savedSearchName(
                    savedSearch,
                    t('search.results.saved.untitled'),
                  );
                  const isEditing = editingSavedSearchId === savedSearch.id;
                  return (
                    <li
                      key={savedSearch.id}
                      className="rounded-card border border-border bg-surface-raised p-2"
                    >
                      {isEditing ? (
                        <form
                          className="space-y-2"
                          onSubmit={(event) => submitRename(event, savedSearch)}
                        >
                          <TextField
                            label={t('search.results.saved.nameLabel')}
                            value={editingSavedSearchName}
                            onChange={(event) =>
                              setEditingSavedSearchName(event.currentTarget.value)
                            }
                          />
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="submit"
                              size="sm"
                              variant="primary"
                              busy={renamingSavedSearchId === savedSearch.id}
                              busyLabel={t('search.results.saved.savingName')}
                            >
                              {t('search.results.saved.saveName')}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="quiet"
                              onClick={cancelRename}
                            >
                              {t('search.results.saved.cancel')}
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-start gap-2">
                          <button
                            type="button"
                            className="min-w-0 rounded-card px-2 py-1 text-left hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            aria-busy={
                              runningSavedSearchId === savedSearch.id ? true : undefined
                            }
                            aria-label={t('search.results.saved.run', { name })}
                            onClick={() => onRunSavedSearch(savedSearch.id)}
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-ink">{name}</span>
                              <span className="block truncate font-mono text-xs text-ink-muted">
                                {savedSearch.queryText}
                              </span>
                            </span>
                            <span role="status" className="sr-only">
                              {runningSavedSearchId === savedSearch.id
                                ? t('search.results.saved.running', { name })
                                : ''}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="inline-flex size-7 shrink-0 items-center justify-center rounded-card text-ink-muted hover:bg-surface hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            aria-label={t('search.results.saved.rename', { name })}
                            onClick={() => startRename(savedSearch)}
                          >
                            <Pencil aria-hidden="true" className="size-4" />
                          </button>
                          <button
                            type="button"
                            className="inline-flex size-7 shrink-0 items-center justify-center rounded-card text-ink-muted hover:bg-surface hover:text-bad focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            aria-label={t('search.results.saved.remove', { name })}
                            disabled={deletingSavedSearchId === savedSearch.id}
                            onClick={() => onDeleteSavedSearch(savedSearch.id)}
                          >
                            <Trash2 aria-hidden="true" className="size-4" />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        </div>

        <section
          id="search-results-panel"
          aria-labelledby="search-results-heading"
          className="min-w-0"
          {...(adjacentTabs.length > 0 ? { role: 'tabpanel' } : {})}
        >
          {adjacentTabs.length > 0 ? (
            <section
              className="mb-5 space-y-3"
              aria-labelledby="adjacent-products-heading"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2
                  id="adjacent-products-heading"
                  className="text-lg font-semibold text-ink"
                >
                  {t('search.results.adjacent.title')}
                </h2>
              </div>

              <div
                role="tablist"
                aria-label={t('search.results.adjacent.title')}
                className="flex flex-wrap gap-2"
              >
                <button
                  id="adjacent-tab-molt"
                  type="button"
                  role="tab"
                  aria-selected={activeAdjacentProductProvider === undefined}
                  aria-controls="search-results-panel"
                  className={
                    activeAdjacentProductProvider === undefined
                      ? 'rounded-card border border-accent bg-accent px-3 py-1.5 text-sm font-medium text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
                      : 'rounded-card border border-border-strong bg-surface-raised px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
                  }
                  onClick={() => onAdjacentProductProviderChange(undefined)}
                >
                  {t('search.results.adjacent.molt')}
                </button>
                {adjacentTabs.map((tab) => (
                  <button
                    key={tab.provider}
                    id={`adjacent-tab-${tab.provider}`}
                    type="button"
                    role="tab"
                    aria-selected={activeAdjacentProductProvider === tab.provider}
                    aria-controls={`adjacent-panel-${tab.provider}`}
                    className={
                      activeAdjacentProductProvider === tab.provider
                        ? 'rounded-card border border-accent bg-accent px-3 py-1.5 text-sm font-medium text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
                        : 'rounded-card border border-border-strong bg-surface-raised px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
                    }
                    onClick={() => onAdjacentProductProviderChange(tab.provider)}
                  >
                    {tab.displayName}
                  </button>
                ))}
              </div>

              {adjacentProductSearchError !== undefined ? (
                <Notice
                  tone="bad"
                  title={t('search.results.adjacent.loadFailed')}
                  body={adjacentProductSearchError}
                  action={
                    <Button type="button" size="sm" onClick={onAdjacentProductRetry}>
                      {t('route.error.retry')}
                    </Button>
                  }
                />
              ) : adjacentProductSearchLoading &&
                activeAdjacentProductProvider !== undefined ? (
                <div aria-label={t('search.results.adjacent.loading')} aria-busy="true">
                  <Skeleton shape="row" count={3} />
                </div>
              ) : activeAdjacentProviderResult !== undefined ? (
                <section
                  id={`adjacent-panel-${activeAdjacentProviderResult.provider}`}
                  role="tabpanel"
                  aria-label={t('search.results.adjacent.panel', {
                    provider: activeAdjacentProviderResult.displayName,
                  })}
                  className="rounded-card border border-border-strong bg-surface-raised p-3"
                >
                  {adjacentProductSearch?.queryText !== undefined ? (
                    <p className="mb-2 text-sm text-ink-muted">
                      {t('search.results.adjacent.providerQuery', {
                        query:
                          adjacentProductSearch.queryText ||
                          t('search.results.adjacent.emptyQuery'),
                      })}
                    </p>
                  ) : null}
                  {activeAdjacentProviderResult.results.length === 0 ? (
                    <p className="text-sm text-ink-muted">
                      {providerPanelMessage(
                        activeAdjacentProviderResult.state,
                        activeAdjacentProviderResult.displayName,
                      )}
                    </p>
                  ) : (
                    <ol className="divide-y divide-border-strong">
                      {activeAdjacentProviderResult.results.map((result) => (
                        <li key={result.id} className="py-3 first:pt-0 last:pb-0">
                          <a
                            href={result.url}
                            target="_blank"
                            rel="noreferrer"
                            className="group flex min-w-0 items-start justify-between gap-3 rounded-card px-2 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            aria-label={t('search.results.adjacent.resultLink', {
                              title: result.title,
                              provider: activeAdjacentProviderResult.displayName,
                            })}
                          >
                            <span className="min-w-0">
                              <span className="block truncate font-medium text-ink group-hover:underline">
                                {result.title}
                              </span>
                              {result.snippet !== null && result.snippet.trim() !== '' ? (
                                <span className="mt-1 block text-sm text-ink-muted">
                                  {result.snippet}
                                </span>
                              ) : null}
                            </span>
                            <ExternalLink
                              aria-hidden="true"
                              className="mt-0.5 size-4 shrink-0 text-ink-muted"
                            />
                          </a>
                        </li>
                      ))}
                    </ol>
                  )}
                </section>
              ) : null}
            </section>
          ) : null}

          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 id="search-results-heading" className="text-lg font-semibold text-ink">
              {t('search.results.results')}
            </h2>
            <p aria-live="polite" className="text-sm text-ink-muted">
              {loading
                ? t('search.searching')
                : t('search.results.visibleCount', { count: cards.length }, cards.length)}
            </p>
          </div>

          {query.trim().length === 0 ? (
            <EmptyState
              tone="neutral"
              size="panel"
              title={t('search.results.enterQueryTitle')}
              body={t('search.results.enterQueryBody')}
            />
          ) : error !== undefined ? (
            <Notice
              tone="bad"
              title={t('search.results.loadFailed')}
              body={error}
              action={
                <Button type="button" size="sm" onClick={onRetry}>
                  {t('route.error.retry')}
                </Button>
              }
            />
          ) : loading ? (
            <div aria-label={t('search.results.loading')} aria-busy="true">
              <Skeleton shape="row" count={6} />
            </div>
          ) : cards.length === 0 ? (
            <EmptyState
              tone="neutral"
              size="panel"
              title={t('search.results.noVisibleMatches')}
              body={t('search.results.noVisibleMatchesBody')}
            />
          ) : (
            <ol className="divide-y divide-border-strong border-y border-border-strong">
              {cards.map((card) => {
                const title = cardTitle(card, t('search.results.untitledCard'));
                const updated = formatDate(card.updatedAt, locale);
                const archived = cardIsArchived(card);
                const due = cardDueStatus(card);
                const dueDate = due === 'none' ? null : formatDueDate(card.dueAt, locale);
                /* ⚠ The badge below sits INSIDE the `<a>`, whose `aria-label`
                   overrides its text content, so a status rendered only as a
                   badge is invisible to assistive technology. The label is the
                   one place the distinction has to be stated in words, and
                   `dueStatusLabel` states it from keys the catalogue already
                   declares (`primitives.cardDate.status.*`) — the same six the
                   card front and the card back route through. It is appended
                   LAST so that a row without the projection keeps the exact
                   accessible name it shipped with. */
                const dueSuffix = due === 'none' ? '' : `, ${dueStatusLabel(t, due)}`;
                return (
                  <li key={card.id} className="relative py-3">
                    <a
                      href={card.path}
                      className="group block rounded-card px-2 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                      aria-label={`${title}, ${resultMeta(card)}${archived ? t('search.results.cardArchivedSuffix') : ''}${card.board.closed ? t('search.results.cardClosedBoardSuffix') : ''}${dueSuffix}`}
                    >
                      <span className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-ink group-hover:underline">
                            {title}
                          </span>
                          <span className="block truncate text-sm text-ink-muted">
                            {resultMeta(card)}
                          </span>
                        </span>
                        <span className="flex shrink-0 flex-wrap gap-2 text-xs text-ink-muted">
                          {archived ? (
                            <span className="inline-flex items-center gap-1 rounded-card border border-border-strong px-2 py-1 text-ink">
                              <Archive aria-hidden="true" className="size-3" />
                              {t('search.results.archived')}
                            </span>
                          ) : null}
                          {card.board.closed ? (
                            <span className="rounded-card border border-border-strong px-2 py-1 text-ink">
                              {t('search.results.closedBoard')}
                            </span>
                          ) : null}
                          {due === 'none' ? null : (
                            <span
                              data-due-status={due}
                              className={`inline-flex items-center gap-1 rounded-card border border-border-strong px-2 py-1 font-medium ${dueToneClass(due)}`}
                            >
                              {due === 'complete' ? (
                                <CheckCircle2 aria-hidden="true" className="size-3" />
                              ) : (
                                <CalendarDays aria-hidden="true" className="size-3" />
                              )}
                              {dueDate ?? dueStatusLabel(t, due)}
                            </span>
                          )}
                          {updated !== null ? (
                            <span className="px-2 py-1">
                              {t('search.results.updated', { date: updated })}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </a>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
